/**
 * Список задач проекта — вторая половина экрана проекта, рядом с доской.
 *
 * Отбор общий с доской по смыслу, но живёт здесь своей копией: экраны
 * открываются по разным адресам и переживают друг друга, а один стор на двоих
 * означал бы, что уход на доску молча меняет то, что видно в списке.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getProject, listColumns, type Project, type ProjectColumn } from '@/features/projects/api'

import {
  listIssues,
  moveIssue,
  setAssignee,
  type IssueRow,
  type IssueSort,
  type IssueSortField,
} from './api'
import { emptyFilters, hasAnyFilter, type IssueFilters } from './filters'
import { groupIssues, type GroupBy } from './grouping'

/** Сколько строк на странице: столько же, сколько показывает макет футера */
export const PER_PAGE = 25

export const useIssuesStore = defineStore('issues', () => {
  const issues = ref<IssueRow[]>([])
  const columns = ref<ProjectColumn[]>([])
  const project = ref<Project | null>(null)
  const projectId = ref('')

  const isLoading = ref(false)
  const error = ref<unknown>(null)

  const filters = ref<IssueFilters>(emptyFilters())
  const sort = ref<IssueSort>({ field: 'key', desc: false })
  const grouping = ref<GroupBy>('status')
  const page = ref(1)
  const total = ref(0)

  /** Отмеченные строки: живут до перезагрузки списка, как в почте */
  const selected = ref<string[]>([])

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PER_PAGE)))

  const isEmpty = computed(() => !isLoading.value && issues.value.length === 0)
  const isFiltered = computed(() => hasAnyFilter(filters.value))

  const groups = computed(() =>
    groupIssues(
      issues.value,
      grouping.value,
      columns.value.map((column) => column.id),
    ),
  )

  const columnOf = (id: string) => columns.value.find((column) => column.id === id) ?? null

  const load = async () => {
    if (!projectId.value) return

    isLoading.value = true
    error.value = null
    selected.value = []

    try {
      const result = await listIssues(
        projectId.value,
        filters.value,
        sort.value,
        page.value,
        PER_PAGE,
      )

      issues.value = result.issues
      total.value = result.total
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Вход на экран: в адресе ключ проекта (`/p/DEV/issues`), а задачи
   * запрашиваются по идентификатору. Колонки приезжают вместе с задачами —
   * без них у строки нет ни имени статуса, ни его цвета.
   */
  const open = async (projectKey: string) => {
    if (project.value?.key === projectKey && issues.value.length) return

    isLoading.value = true
    error.value = null
    project.value = null
    issues.value = []
    columns.value = []
    page.value = 1
    // Отбор принадлежит экрану проекта: на другой проект он не переезжает.
    filters.value = emptyFilters()

    try {
      const found = await getProject(projectKey)

      project.value = found
      projectId.value = found.id

      const [result, projectColumns] = await Promise.all([
        listIssues(found.id, filters.value, sort.value, 1, PER_PAGE),
        listColumns(found.id),
      ])

      issues.value = result.issues
      total.value = result.total
      columns.value = projectColumns
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /** Новый отбор — всегда с первой страницы: на третьей его может не хватить и на одну */
  const applyFilters = async (next: IssueFilters) => {
    filters.value = next
    page.value = 1

    await load()
  }

  /**
   * Повторный клик по той же колонке переворачивает порядок — так сортируют
   * везде, и объяснять это не нужно.
   */
  const applySort = async (field: IssueSortField) => {
    sort.value =
      sort.value.field === field
        ? { field, desc: !sort.value.desc }
        : { field, desc: field === 'priority' }
    page.value = 1

    await load()
  }

  const setPage = async (next: number) => {
    const clamped = Math.min(Math.max(1, next), pageCount.value)

    if (clamped === page.value) return

    page.value = clamped

    await load()
  }

  const setGrouping = (next: GroupBy) => {
    grouping.value = next
  }

  const toggleSelected = (id: string) => {
    selected.value = selected.value.includes(id)
      ? selected.value.filter((entry) => entry !== id)
      : [...selected.value, id]
  }

  const toggleAll = () => {
    selected.value = selected.value.length === issues.value.length
      ? []
      : issues.value.map((issue) => issue.id)
  }

  /**
   * Правки из строки применяются сразу и откатываются при отказе: список —
   * рабочий стол, и строка, которая меняется через полсекунды, читается как
   * «не сработало». Перезапроса нет намеренно: он сдвинул бы строки под рукой
   * у того, кто размечает несколько задач подряд.
   */
  const patch = async (id: string, change: Partial<IssueRow>, send: () => Promise<void>) => {
    const issue = issues.value.find((entry) => entry.id === id)

    if (!issue) return

    const saved = { ...issue }

    Object.assign(issue, change)

    try {
      await send()
    } catch (failure) {
      Object.assign(issue, saved)

      throw failure
    }
  }

  /** Смена статуса — это перенос в колонку: другого хранилища статуса нет */
  const moveTo = (id: string, columnId: string) =>
    patch(id, { columnId }, () => moveIssue(id, columnId))

  const assignTo = (id: string, assigneeId: string, assigneeName: string) =>
    patch(id, { assigneeId, assigneeName }, () => setAssignee(id, assigneeId))

  return {
    issues,
    columns,
    project,
    projectId,
    isLoading,
    error,
    filters,
    sort,
    grouping,
    page,
    total,
    pageCount,
    selected,
    isEmpty,
    isFiltered,
    groups,
    columnOf,
    open,
    load,
    applyFilters,
    applySort,
    setPage,
    setGrouping,
    toggleSelected,
    toggleAll,
    moveTo,
    assignTo,
  }
})
